import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { usePublishPageLanguage } from "@/lib/page-language";
import type { Session } from "@supabase/supabase-js";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  checkInviteStatus,
  redeemChildInvite,
  redeemGuardianInvite,
  redeemStudentInvite,
} from "@/lib/cabinet.functions";
import type { InviteStatus } from "@/lib/cabinet.functions";
import { setAccountLocale } from "@/lib/locale.functions";
import { amberPill, outlinePill } from "@/components/cabinet/CabinetCards";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Child-login redeem page. The parent shares /cabinet/join?code=… via a
// messenger; the child opens it, signs in with their OWN magic-link (which
// redirects back here so the code context survives), then confirms to link.
export const Route = createFileRoute("/cabinet_/join")({
  head: () => ({
    meta: [{ title: "Yolutap" }, { name: "robots", content: "noindex" }],
    // Brand fonts, same loader /cabinet uses. This route is a sibling file, so
    // it never inherited D1's font links — headings fell back to ui-sans-serif.
    links: [
    ],
  }),
  component: JoinPage,
});

type Lang = "ru" | "az" | "en";
// Which invitation the link carries. Drives the copy only — the redeem
// function is chosen from the same value.
type InviteKind = "child" | "guardian" | "tutor";
const LANGS = ["ru", "az", "en"] as const;
const isLang = (l: string | null | undefined): l is Lang => LANGS.includes(l as Lang);

// Locale BEFORE login — there's no profile to read yet, so this screen resolves
// it once and drives the body text, <html lang> and the title from that single
// value (they used to disagree: Russian title over English body).
//
// Every invite link carries ?lang (see the invite controls in cabinet.tsx), so
// the inviter's locale normally decides. Only when it's missing do we look at
// the browser — and only for the languages this audience actually reads, since
// an Azerbaijani parent on an en-US phone must not be handed English. Anything
// else falls back to Azerbaijani, the product's main language.
function resolveLang(url: URL): Lang {
  const explicit = url.searchParams.get("lang");
  if (isLang(explicit)) return explicit;
  const nav = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : null;
  if (nav === "az" || nav === "ru") return nav;
  return "az";
}

// Error codes with nothing left to try — hide the action, offer a way back.
const TERMINAL = new Set(["INVALID", "USED", "REVOKED", "EXPIRED", "CHILD_TAKEN", "ALREADY_LINKED"]);

const JT: Record<Lang, Record<string, string>> = {
  ru: {
    title: "Присоединиться к кабинету",
    need_login: "Войдите своим email, чтобы привязать аккаунт.",
    email: "Email",
    get_link: "Получить ссылку",
    check_mail: "Мы отправили ссылку на почту. Откройте её на этом устройстве.",
    confirm_body: "Подтвердите привязку вашего аккаунта к профилю ребёнка.",
    join: "Присоединиться",
    ok: "Готово! Аккаунт привязан.",
    to_cabinet: "Перейти в кабинет →",
    back_cabinet: "← в кабинет",
    page_title: "Присоединиться — Yolutap",
    no_code: "Ссылка без кода приглашения.",
    err_INVALID: "Код не найден.",
    err_USED: "Код уже использован.",
    err_REVOKED: "Приглашение отозвано. Попросите родителя прислать новое.",
    err_EXPIRED: "Срок приглашения истёк. Попросите новое.",
    err_CHILD_TAKEN: "К этому профилю уже привязан другой аккаунт.",
    err_generic: "Не удалось присоединиться. Попробуйте позже.",
    tutor_title: "Приглашение от репетитора",
    tutor_confirm: "Ваш репетитор пригласил вас пройти тест. Результаты придут вам — репетитор их не увидит, он видит только, что тест пройден.",
    tutor_ok: "Готово! Можно проходить тест. Результат будет ваш.",
    guardian_title: "Кабинет вашего ребёнка",
    guardian_confirm: "Подтвердите привязку вашего аккаунта как родителя — вы получите доступ к профилю ребёнка.",
    guardian_ok: "Готово! Профиль ребёнка привязан к вашему аккаунту.",
    err_ALREADY_LINKED: "К этому ребёнку уже привязан другой родитель.",
  },
  az: {
    title: "Kabinetə qoşul",
    need_login: "Hesabı bağlamaq üçün öz email-inizlə daxil olun.",
    email: "Email",
    get_link: "Keçid al",
    check_mail: "Poçtunuza keçid göndərdik. Onu bu cihazda açın.",
    confirm_body: "Hesabınızı uşağın profilinə bağlamağı təsdiqləyin.",
    join: "Qoşul",
    ok: "Hazırdır! Hesab bağlandı.",
    to_cabinet: "Kabinetə keç →",
    back_cabinet: "← kabinetə",
    page_title: "Kabinetə qoşul — Yolutap",
    no_code: "Dəvət kodu olmayan keçid.",
    err_INVALID: "Kod tapılmadı.",
    err_USED: "Kod artıq istifadə olunub.",
    err_REVOKED: "Dəvət ləğv edilib. Valideyndən yenisini istəyin.",
    err_EXPIRED: "Dəvətin vaxtı bitib. Yenisini istəyin.",
    err_CHILD_TAKEN: "Bu profilə artıq başqa hesab bağlanıb.",
    err_generic: "Qoşulmaq alınmadı. Sonra yenidən cəhd edin.",
    tutor_title: "Repetitordan dəvət",
    tutor_confirm: "Repetitorunuz sizi testi keçməyə dəvət edib. Nəticələr sizə gələcək — repetitor onları görmür, yalnız testin keçildiyini görür.",
    tutor_ok: "Hazırdır! Testi keçə bilərsiniz. Nəticə sizin olacaq.",
    guardian_title: "Övladınızın kabineti",
    guardian_confirm: "Valideyn kimi hesabınızın bağlanmasını təsdiqləyin — övladınızın profilinə giriş əldə edəcəksiniz.",
    guardian_ok: "Hazırdır! Övladınızın profili hesabınıza bağlandı.",
    err_ALREADY_LINKED: "Bu uşağa artıq başqa valideyn bağlanıb.",
  },
  en: {
    title: "Join the cabinet",
    need_login: "Sign in with your email to link your account.",
    email: "Email",
    get_link: "Get link",
    check_mail: "We sent a link to your email. Open it on this device.",
    confirm_body: "Confirm linking your account to the child’s profile.",
    join: "Join",
    ok: "Done! Account linked.",
    to_cabinet: "Go to cabinet →",
    back_cabinet: "← to cabinet",
    page_title: "Join the cabinet — Yolutap",
    no_code: "This link has no invite code.",
    err_INVALID: "Code not found.",
    err_USED: "This code was already used.",
    err_REVOKED: "The invite was revoked. Ask the parent for a new one.",
    err_EXPIRED: "The invite expired. Ask for a new one.",
    err_CHILD_TAKEN: "Another account is already linked to this profile.",
    err_generic: "Could not join. Try again later.",
    tutor_title: "Invitation from your tutor",
    tutor_confirm: "Your tutor invited you to take the test. The results come to you — your tutor never sees them, only that the test was taken.",
    tutor_ok: "Done! You can take the test now. The result will be yours.",
    guardian_title: "Your child’s cabinet",
    guardian_confirm: "Confirm linking your account as the parent — you’ll get access to your child’s profile.",
    guardian_ok: "Done! Your child’s profile is linked to your account.",
    err_ALREADY_LINKED: "Another parent is already linked to this child.",
  },
};

function JoinPage() {
  const navigate = useNavigate();
  const redeemChild = useServerFn(redeemChildInvite);
  const redeemGuardian = useServerFn(redeemGuardianInvite);
  const redeemStudent = useServerFn(redeemStudentInvite);
  const checkStatus = useServerFn(checkInviteStatus);
  const captureLocale = useServerFn(setAccountLocale);
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  // Which invite family this link belongs to. ?role= carries it; anything
  // unrecognised is the original child invite, which had no marker.
  const [kind, setKind] = useState<InviteKind>("child");
  const [code, setCode] = useState<string | null>(null);
  const [lang, setLang] = useState<Lang>("az");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Raw error code (e.g. "USED") so we can tell terminal from retryable; the
  // display text is looked up from JT. Set by redeem / sign-in failures.
  const [errKey, setErrKey] = useState<string | null>(null);
  // Pre-login code check (B2): the code's status before the visitor logs in.
  const [checking, setChecking] = useState(false);
  const [codeStatus, setCodeStatus] = useState<InviteStatus | null>(null);
  // The language this screen resolved at mount, kept out of React state so the
  // capture below can't read a stale render. Invite links always carry ?lang.
  const entryLang = useRef<Lang>("az");
  // <html lang> — тем же языком, что текст и заголовок вкладки.
  usePublishPageLanguage(lang);
  const captured = useRef(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const c = url.searchParams.get("code");
    const roleParam = url.searchParams.get("role");
    const inviteKind: InviteKind =
      roleParam === "guardian" ? "guardian" : roleParam === "tutor" ? "tutor" : "child";
    const guardian = inviteKind === "guardian";
    setCode(c);
    setKind(inviteKind);
    // One resolved locale drives all three: body copy, <html lang>, title.
    const resolved = resolveLang(url);
    entryLang.current = resolved;
    setLang(resolved);
    document.title = JT[resolved].page_title;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    // Validate the code up front so a dead link is caught BEFORE the login form.
    // Read-only status only — no child data is exposed to an unauthenticated
    // visitor. A network hiccup leaves codeStatus null so redeem still decides.
    if (c) {
      setChecking(true);
      checkStatus({ data: { code: c, guardian } })
        .then((r) => setCodeStatus(r.status))
        .catch(() => setCodeStatus(null))
        .finally(() => setChecking(false));
    }
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Capture the entry language onto the account. This screen has its own door:
  // its magic link comes back HERE (emailRedirectTo: window.location.href), so
  // it never passes through /auth/callback and would otherwise be the one way
  // to create an account with no language attached. Write-once server-side;
  // the ref keeps a second render from re-sending it.
  useEffect(() => {
    if (!session || captured.current) return;
    captured.current = true;
    void captureLocale({ data: { locale: entryLang.current } }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const t = JT[lang];
  const toCabinet = () => navigate({ to: "/cabinet" });
  // A code the up-front check already rejected (any non-"valid" status).
  const preInvalid: InviteStatus | null =
    codeStatus && codeStatus !== "valid" ? codeStatus : null;

  const sendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrKey(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true, emailRedirectTo: window.location.href },
      });
      if (error) throw error;
      setSent(true);
    } catch {
      setErrKey("generic");
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!code) return;
    setBusy(true);
    setErrKey(null);
    try {
      const fn =
        kind === "guardian" ? redeemGuardian : kind === "tutor" ? redeemStudent : redeemChild;
      await fn({ data: { code } });
      setDone(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      setErrKey(msg || "generic");
    } finally {
      setBusy(false);
    }
  };

  // Prominent error plaque — soft box, border, icon, ≥16px text. On terminal
  // errors it also offers a way back (nothing left to retry here).
  // The plaque carries the error; the way out sits BESIDE it, not inside — a
  // button nested in the red box reads as part of the failure. Secondary pill:
  // it's the only action, but the screen has nothing worth pushing towards.
  const plaqueText = (text: string, back: boolean) => (
    <div className="space-y-3">
      <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4">
        <div className="flex items-start gap-2.5">
          <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
          <p className="text-base leading-snug text-destructive">{text}</p>
        </div>
      </div>
      {back && (
        <button
          type="button"
          onClick={toCabinet}
          // text-base! — outlinePill ships text-sm and wins the cascade
          // otherwise; mobile copy on this screen must stay at 16px.
          className={`${outlinePill} min-h-[44px] w-full text-base!`}
        >
          {t.back_cabinet}
        </button>
      )}
    </div>
  );
  const plaque = (key: string, back: boolean) =>
    plaqueText(t[`err_${key}`] ?? t.err_generic, back);

  return (
    // D1 shell: cream page, bordered card instead of a shadowed white one,
    // serif heading. The error plaque stays red on purpose — D1 has no alarm
    // colour, and an error must read as one.
    <div className="flex min-h-screen items-center justify-center bg-yt-cream p-6 font-yt-sans text-yt-ink">
      <div className="w-full max-w-md">
        {/* 18px, matching CabinetHeader — the project overrides Tailwind's
            scale (--radius: 0.875rem), so rounded-2xl lands at 22px, past the
            16–18px D1 allows for cards. */}
        <div className="rounded-[18px] border border-yt-line bg-white p-5 md:p-6">
          <h1 className="font-yt-serif text-xl font-semibold text-yt-ink">
            {t[`${kind}_title`] ?? t.title}
          </h1>
          <div className="mt-4 space-y-4">
            {!code ? (
              plaqueText(t.no_code, true)
            ) : done ? (
              <div className="space-y-3">
                <p className="text-base text-yt-ink">{t[`${kind}_ok`] ?? t.ok}</p>
                <button
                  type="button"
                  onClick={toCabinet}
                  className={`${amberPill} min-h-[44px] w-full text-base!`}
                >
                  {t.to_cabinet}
                </button>
              </div>
            ) : !ready || checking ? (
              <p className="text-base text-yt-ink/60">…</p>
            ) : preInvalid ? (
              // B2: the up-front check rejected the code — show why, skip login.
              plaque(preInvalid, true)
            ) : session ? (
              <div className="space-y-3">
                <p className="text-base text-yt-ink/70">
                  {t[`${kind}_confirm`] ?? t.confirm_body}
                </p>
                {errKey && plaque(errKey, TERMINAL.has(errKey))}
                {!(errKey && TERMINAL.has(errKey)) && (
                  // The only action on the screen, so it takes the amber with
                  // dark text — white on amber is 1.9:1 and forbidden.
                  <button
                    type="button"
                    onClick={join}
                    disabled={busy}
                    className={`${amberPill} min-h-[44px] w-full text-base! disabled:opacity-50`}
                  >
                    {busy ? "…" : t.join}
                  </button>
                )}
              </div>
            ) : sent ? (
              <p className="text-base text-yt-ink/70">{t.check_mail}</p>
            ) : (
              <form onSubmit={sendLink} className="space-y-3">
                <p className="text-sm text-muted-foreground">{t.need_login}</p>
                <div className="space-y-2">
                  <Label htmlFor="join-email">{t.email}</Label>
                  <Input
                    id="join-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                {errKey && plaque(errKey, false)}
                <button
                  type="submit"
                  disabled={busy || !email}
                  className={`${amberPill} min-h-[44px] w-full text-base! disabled:opacity-50`}
                >
                  {busy ? "…" : t.get_link}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
